<script setup>

  const { find } = useStrapi4()
  const employers = reactive({
    ...(await useAsyncData(
      async () => {
        const res = await find('employers', {
          fields: ['name', 'slug'],
          filters: { visible: true },
          pagination: { limit: 13, start: 0, withCount: false },
          populate: {
            careerSite: { fields: ['active', 'slug'] },
            cover: { fields: ['url'] },
            logo: { fields: ['url'] }
          },
          sort: 'random'
        })
        return res.data
      },
      {
        default: () => []
      }
    ))
  })
  const testimonials =
  [
    {
      author: 'Paul Andrieux',
      role: 'Responsable RH chez Mairie de Soyaux',
      content: 'Grâce à notre page créée par l’équipe Profil Public, nous avons réussi le pari de gagner en visibilité ! Notre page recrutement est vraiment modernisée et nous a permis de simplifier notre processus de recrutement.',
      linkedin: 'https://www.linkedin.com/in/paul-andrieux-7a6b09270/overlay/about-this-profile/',
      photo: '/images/paul-andrieux-400x400.webp'
    },
    {
      author: 'Steevie Kazal',
      role: 'DGA Ressources / Modernisation de l’Administration Communauté de Communes de l’Ouest Guyanais',
      content: 'Auparavant, notre équipe devait saisir nos offres sur notre site web, Emploi territorial, l’Apec, etc. Avec cette solution, nous n’avons plus qu’à déposer notre fiche de poste en pdf et l’outil s’occupe du reste.',
      linkedin: '',
      photo: '/images/steevie-kazal-400x400.webp'
    },
    {
      author: 'Gilles Intem',
      role: 'DRH  Communauté d’agglomération Maubeuge-Val de Sambre',
      content: 'Grâce à Profil Public, nous recevons un plus large panel de candidatures sur des secteurs d’activité ordinairement tendus. Leur communication moderne et dynamique permet de toucher des profils différents de ceux qui fréquentent les plateformes traditionnelles.',
      linkedin: '',
      photo: '/images/gilles-intem-200x200.webp'
    },
  ]
  const problematics = [
    {
      icon: '🤔',
      text: 'Les candidats en quête de sens ne pensent pas toujours spontanément au secteur public.'
    },
    {
      icon: '❌',
      text: 'Manque de visibilité, image dégradée, fiches de poste trop longues, jargon administratif...'
    },
    {
      icon: '🔎',
      text: 'Résultat : vos annonces passent souvent inaperçues dans un paysage où la communication RH prend de plus en plus d’ampleur.'
    },
    {
      icon: '⌛️',
      text: 'Pendant ce temps, la rédaction, la diffusion et la gestion des candidatures rendent chaque recrutement plus chronophage pour vos équipes.'
    }
  ]
</script>

<template>
  <Page code="about-us">
    <section class="flex flex-col justify-evenly lg:h-[calc(100vh-90px)] p-10">
      <div class="flex flex-row justify-center flex-wrap w-full mt-10 mb-10">
        <div class="w-[450px] flex flex-col xs:items-start items-center xs:text-start text-center justify-center mb-3">
          <h1 class="font-special font-bold text-2xl mb-2">
            Profil Public : la solution attractivité du secteur public
          </h1>
          <p class="font-special mb-10 w-10/12">
            + de 40 000 candidats prêts à rejoindre la fonction publique sont déjà sur <NuxtLink class="underline" to="https://profilpublic.fr">profilpublic.fr</NuxtLink>
          </p>
          <NuxtLink class="self-center md:self-start" to="/contact">
            <Button>Échangeons ensemble</Button>
          </NuxtLink>
        </div>
        <div class="xs:w-[400px] w-[300px] xs:mr-0 mt-10">
          <NuxtImg src="/images/ats-example.webp"></NuxtImg>
        </div>
      </div>
      <div class="w-[90%] lg:w-[70%] self-center mb-5">
        <p class="font-special mb-2">
          Ils nous font confiance :
        </p>
        <div class="flex flex-row md:justify-between justify-center items-center gap-3 flex-wrap ml-auto">
          <div class="" v-for="employer in employers.data" :key="employer.id">
            <div
              class="w-[60px] rounded-full bg-cover bg-center pt-[60px] shadow-md"
              v-if="employer.logo?.url"
              :style="{
                backgroundImage: employer.logo?.url ? `url('${useStrapiMedia(employer.logo.url)}')` : 'images/no-image.webp'
              }"
            />
          </div>
        </div>
      </div>
    </section>
    <section class="flex justify-center bg-zinc-200 p-10">
      <div class="shadow-md w-[90%] bg-white rounded-lg p-5">
        <h2 class="font-special font-bold text-lg text-center mb-4">
          Manque de visibilité, image dégradée, jargon administratif :<br/>
          les candidats en quête de sens ne pensent pas spontanément au secteur public.
        </h2>
        <div class="flex flex-row justify-evenly flex-wrap lg:flex-nowrap gap-3">
          <div v-for="problematic in problematics" :key="problematic.text" class="flex flex-col items-center w-[280px]">
            <p class="text-2xl">{{ problematic.icon }}</p>
            <p class="font-special text-sm ">{{ problematic.text }}</p>
          </div>
        </div>
      </div>
    </section>
    <section class="flex flex-row justify-center flex-wrap md:flex-nowrap p-10">
        <div class="font-special w-[600px] flex flex-col justify-center mb-3">
          <p class="font-bold mb-4">
            Afin de recruter autrement dans la fonction publique, <br/>
            il existe pourtant une solution qui permet de :
          </p>
          <ul class="list-disc list-inside p-2">
            <li class="mb-4">transformer vos fiches de poste en annonces claires et engageantes.</li>
            <li class="mb-4">publier toutes vos offres d’emploi en seulement quelques clics.</li>
            <li class="mb-4">projeter les candidats dans votre environnement de travail.</li>
            <li class="mb-4">attirer des candidats motivés, alignés avec vos besoins et votre mission.</li>
          </ul>
        </div>
        <div class="shadow-md bg-tarawera-900 w-[380px] rounded-2xl text-white font-special flex flex-col justify-between items-center">
          <div class="p-5 px-7 self-start">
            <h2 class="text-md font-bold mb-2">Solution attractivité</h2>
            <p class="text-sm font-bold">Standard</p>
            <ul class="list-disc list-inside p-2 text-sm">
              <li>
                Page emploi à vos couleurs
              </li>
              <li>
                Publication illimitée d'offres d’emploi
              </li>
              <li>
                Multidiffusion
              </li>
              <li>
                Espace de gestion collaboratif
              </li>
              <li>
                Automatisations (réponses aux candidats)
              </li>
            </ul>
            <p class="text-sm font-bold">Premium</p>
            <ul class="list-disc list-inside p-2 text-sm">
              <li>
                CVthèque
              </li>
              <li>
                Statistiques
              </li>
            </ul>
          </div>
          <div class="bg-white w-full p-5 px-7 flex flex-row items-center justify-between">
            <NuxtLink class="" to="/contact">
              <Button>Parlons-en</Button>
            </NuxtLink>
            <div class="flex flex-row items-center gap-1">
              <p class="text-tarawera-900 italic text-center">
                Référencé
              </p>
              <NuxtImg width="40px" height="40px" src="/images/logo-ugap.svg"></NuxtImg>
            </div>
          </div>
        </div>
    </section>
    <section class="mb-10 p-10">
      <div class="flex flex-row justify-center items-baseline w-full">
        <Quote class="mx-auto max-w-screen-lg px-4 py-8 font-special">
          Administratrice territoriale, j’ai passé 5 ans au service de la fonction publique. Mais à chaque fois, je constatais le même problème : des offres d’emploi longues, techniques et peu engageantes… Convaincue qu’il était possible d’attirer autrement, j’ai créé Profil Public en 2018. Aujourd’hui, nous rassemblons plus de 40 000 candidats et proposons une solution attractivité clé en main pour aider les employeurs publics à recruter.
          <template #profile>
            <Profile
              :profile="{
                linkedin: 'sigrid-berger',
                name: 'Sigrid Berger',
                photo: '/images/sigrid-150x150.png',
                title: 'Fondatrice de Profil Public & administratrice territoriale'
              }"
              extraSmall
              row
              title-size="sm"
            />
          </template>
        </Quote>
      </div>
    </section>
    <section class="font-special flex flex-col justify-center items-center p-10">
      <h2 class="text-xl font-bold self-center mb-10">Nos 4 leviers pour vous faire gagner en attractivité</h2>
      <div class="flex flex-row items-center justify-center flex-wrap lg:flex-nowrap w-[90%] gap-3">
        <div class="flex flex-col gap-3 text-sm">
          <ul class="list-decimal flex flex-col">
            <li class="font-bold">
              Des offres d’emploi qui donnent envie de postuler
            </li>
            <p class="mb-2">
              Grâce à notre outil de publication automatisé, transformez vos offres en annonces attractives en 1 clic.
            </p>
            <li class="font-bold">
              Une communication RH qui parle aux candidats
            </li>
            <p class="mb-2">
              Grâce à une page emploi personnalisée à vos couleurs pour capter l’attention des candidats qui partagent vos valeurs.
            </p>
            <li class="font-bold">
              Une diffusion qui dépasse les canaux classiques
            </li>
            <p class="mb-2">
              Grâce à une diffusion ciblée pour diversifier vos viviers et toucher les bons candidats là où ils sont.
            </p>
            <li class="font-bold">
              Un parcours simplifié pour accélérer vos recrutements
            </li>
            <p class="mb-2">
              Grâce à nos fonctionnalités qui simplifient chaque étape : publication, diffusion, gestion des candidatures, réponses aux candidats...
            </p>
          </ul>
        </div>
        <iframe
            class="aspect-video w-[70%] rounded-xl border-2"
            src="https://www.youtube-nocookie.com/embed/bPRgWTJUTXs"
            title="YouTube video player Profil Public - Notre solution d’attractivité RH pour le secteur public
"
            frameborder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowfullscreen
          />
      </div>
    </section>
    <section class="bg-tarawera-950 flex justify-center font-special items-center p-10">
      <div class="bg-white rounded-xl font-bold p-4 leading-6 ">
        💥 +30% de candidatures qualifiées <br/>
        ⏳ 30% de temps économisé sur chaque recrutement <br/>
        🚀 Une visibilité multipliée par 5 sur LinkedIn <br/>
        🎯 Un impact direct auprès de + 40 000 talents en recherche active <br/>
      </div>
    </section>
    <section class="font-special flex flex-col justify-center items-center p-10">
      <h2 class="text-xl font-bold self-center mb-10">Plus de 100 institutions nous font confiance</h2>
      <div class="flex flex-row justify-center xl:justify-between flex-wrap gap-10 mb-10">
        <div class="bg-zinc-200 rounded-xl flex flex-col justify-between xs:w-[420px] w-[98%] " v-for="testimonial in testimonials" :key="testimonial.author">
          <div class="py-3 px-4 ">
            <p class="text-sm">« {{ testimonial.content }} »</p>
          </div>
          <div class="bg-tarawera-900 py-3 px-3 rounded-b-xl text-white flex flex-row items-center gap-2">
            <div class="relative">
              <div
                class="rounded-full bg-zinc-300 bg-cover bg-center"
                :class="`h-12 w-12`"

                :style="{
                  backgroundImage: testimonial.photo ? `url('${testimonial.photo}')` : undefined
                }"
              />
            </div>
            <div>
              <p class="text-sm">{{ testimonial.author }}</p>
              <p class="text-xs">{{ testimonial.role }}</p>
            </div>
          </div>
        </div>
      </div>
      <h3 class="font-bold">Vous souhaitez vous démarquer pour diversifier vos talents ?</h3>
      <p class="mb-5">Profitez de 2 mois sans engagement, en toute liberté.</p>
      <NuxtLink class="" to="/contact">
        <Button>Contacter l'équipe</Button>
      </NuxtLink>
    </section>
  </Page>
</template>
